import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";

interface ConversationPreviewProps {
  id: string;
  name: string;
  avatar?: string;
  lastMessage: string;
  lastMessageTime: Date;
  unreadCount?: number;
  isOnline?: boolean;
  onClick?: () => void;
}

export function ConversationPreview({
  id,
  name,
  avatar,
  lastMessage,
  lastMessageTime,
  unreadCount = 0,
  isOnline = false,
  onClick,
}: ConversationPreviewProps) {
  const initials = name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <Card
      className={cn(
        "border-0 border-b rounded-none last:border-b-0",
        "active:bg-accent transition-colors cursor-pointer"
      )}
      onClick={onClick}
    >
      <div className="flex items-center gap-4 p-4 min-h-[72px]">
        {/* Avatar with online indicator */}
        <div className="relative shrink-0">
          <Avatar className="h-14 w-14">
            <AvatarImage src={avatar} alt={name} />
            <AvatarFallback className="bg-primary/10 text-primary font-semibold">
              {initials}
            </AvatarFallback>
          </Avatar>
          {isOnline && (
            <div className="absolute bottom-0 right-0 h-3 w-3 rounded-full bg-green-500 border-2 border-background" />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-semibold text-base truncate">{name}</h3>
            <span className="text-xs text-muted-foreground shrink-0 ml-2">
              {formatDistanceToNow(lastMessageTime, { addSuffix: true })}
            </span>
          </div>
          <p className="text-sm text-muted-foreground truncate">
            {lastMessage}
          </p>
        </div>

        {/* Unread badge */}
        {unreadCount > 0 && (
          <Badge
            variant="default"
            className="shrink-0 h-6 min-w-6 px-2 rounded-full text-xs font-semibold"
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </Badge>
        )}
      </div>
    </Card>
  );
}